/* ----------------------------------------
    Imports
---------------------------------------- */

import { Component, Vue } from 'vue-property-decorator';
import { applyConfig } from '@config';
import { camelCase, kebabCase, isEmpty, merge } from 'lodash';
import { mapGetters } from 'vuex';
import { ObjectHelper } from '@fruitpackage/helpers';

/* ----------------------------------------
    Component
---------------------------------------- */

@Component({
    data() {
        return {
            config: applyConfig.config,
        };
    },

    computed: {
        ...mapGetters({
            current: 'project/current',
        }),

        action() {
            return (this.$route.params.project === 'new') ? 'new' : 'edit';
        },

        projectId() {
            return this.$route.params.project;
        },

        isNew() {
            return (this.action === 'new');
        },

        isProject() {
            return !this.isNew;
        },

        groupKey() {
            return this.$route.params.group;
        },

        groupKeys() {
            return Object.keys(this.groups);
        },

        group() {
            return this.groups[this.groupKey];
        },

        groups() {
            return this.config[this.action];
        },

        groupIndex() {
            return this.groupKeys.indexOf(this.groupKey);
        },

        stepKey() {
            return camelCase(this.$route.params.step);
        },

        stepKeys() {
            return Object.keys(this.group.steps);
        },

        step() {
            return this.group.steps[this.stepKey];
        },

        stepIndex() {
            return this.stepKeys.indexOf(this.stepKey);
        },

        combinedGroups() {
            return merge(Object.assign({}, this.config['new'], Object.assign({}, this.config['edit'])));
        },

        applyProgress() {
            const progress = {};
            const keys = Object.keys(this.combinedGroups);
            keys.forEach(groupKey => {
                const group = this.combinedGroups[groupKey];
                progress[groupKey] = {
                    number: keys.indexOf(groupKey) + 1,
                    done: (keys.indexOf(this.groupKey) > keys.indexOf(groupKey)),
                    current: (keys.indexOf(this.groupKey) === keys.indexOf(groupKey)),
                    title: group.title,
                };
            });
            return progress;
        },
    },

    methods: {
        groupLinkByKey(projectId: string, key: string) {
            const stepStart = this.groups[key].startStep;
            return this.stepLinkByKey(projectId, key, stepStart);
        },

        groupIndexByKey(key: string) {
            return this.groupKeys.indexOf(key);
        },

        groupCompleteByKey(key: string) {
            return (this.groupIndex > this.groupIndexByKey(key));
        },

        stepLinkByKey(projectId: string, groupKey: string, stepKey: string) {
            return `/apply/${ projectId }/${ groupKey }/${ stepKey }`;
        },

        validateRoute(to, prevValidate) {
            const action = (to.params.project === 'new') ? 'new' : 'edit';
            const projectId = (action === 'new') ? 'new' : to.params.project;
            const groups = applyConfig.config[action];
            const groupKey = to.params.group;
            const group = groups[groupKey];
            const stepKey = camelCase(to.params.step);
            const step = group.steps[stepKey];

            let response = {
                valid: true,
                redirect: ''
            };
            
            if (this.isProject && !this.current) 
                response.redirect = '/projects'

            if (prevValidate && step.skipOnPrev)
                response.redirect = true;

            if (!response.redirect && step)
                response.redirect = this.skipRedirect(step, projectId);

            if (response.redirect)
                response.valid = false;

            return response;
        },

        skipRedirect(step, projectId) {
            if (!step.skip) 
                return '';

            // Skip if user has no organisation
            if (step.skip.noOrganisations && isEmpty(this.stateOrganisations))
                return `/apply/${ projectId }/${ step.skip.noOrganisations }`;

            // Skip if project organisation is in formation
            if (step.skip.organizationInFormation && this.current.organization_in_formation)
                return `/apply/${ projectId }/${ step.skip.organizationInFormation }`;

            // Skip if project organisation is not in formation
            if (step.skip.organizationNotInFormation && !this.current.organization_in_formation)
                return `/apply/${ projectId }/${ step.skip.organizationNotInFormation }`;

            // Skip if project starting date is valid
            if (step.skip.starting_date_valid && this.current.starting_date_valid)
                return `/apply/${ projectId }/${ step.skip.starting_date_valid }`;

            // Skip if project organisation is a community
            if (step.skip.isCommunity && this.current.organisation.community)
                return `/apply/${ projectId }/${ step.skip.isCommunity }`;

            // Skip if project organisation is not a community
            if (step.skip.isNoCommunity && !this.current.organisation.community)
                return `/apply/${ projectId }/${ step.skip.isNoCommunity }`;

            // Skip if project organisation is a community
            if (step.skip.statutenExists && this.current.organisation.statutes)
                return `/apply/${ projectId }/${ step.skip.statutenExists }`;

            if (step.skip.noAuthorizedToSignRequired && this.current.noAuthorizedToSignRequired)
                return `/apply/${ projectId }/${ step.skip.noAuthorizedToSignRequired }`;

            // Skip if user is admin
            if (step.skip.isAdmin && this.current.organisation.admin)
                return `/apply/${ projectId }/${ step.skip.isAdmin }`;

            // Skip if user is no admin
            if (step.skip.isNoAdmin && !this.current.organisation.admin)
                return `/apply/${ projectId }/${ step.skip.isNoAdmin }`;

            // Skip if user is no admin and given value is set
            if (step.skip.isNoAdminAndValueIsSet && !this.current.organisation.admin) {
                const settings = step.skip.isNoAdminAndValueIsSet;
                const value = ObjectHelper.pathValue(this.current, settings[0]);
                if (value)
                    return `/apply/${ projectId }/${ settings[1] }`;
            }

            // Skip if user is no admin and given file exists
            if (step.skip.isNoAdminAndFileIsSet && !this.current.organisation.admin) {
                const settings = step.skip.isNoAdminAndFileIsSet;
                const id = (settings[0] === 'organisation') ? this.current.organisation_id : this.current.id;

                if (this.current.organisation_files.includes(settings[1]))
                    return `/apply/${ projectId }/${ settings[2] }`;
            }

            return '';
        },

        getPrevNextStep(groupKey, stepKeys, stepIndex, next) {
            let result = false;

            for (let i = 1; i < stepKeys.length + 1; i++) {
                const index = (next) ? stepIndex + i : stepIndex - i;
                const stepKey = kebabCase(stepKeys[index]);

                if (!stepKey) break;

                const to = this.stepLinkByKey(this.projectId, groupKey, stepKey);

                if (!to) break;

                const resolved = this.$router.resolve(to);
                const validate = this.validateRoute(resolved.route, true);

                if (validate.valid) {
                    result = to;
                    break;
                }
            }

            return result;
        },
    }
})

/* ----------------------------------------
    Export
---------------------------------------- */

export default class ApplyMixin extends Vue {}